import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ArrowUpRight } from "lucide-react";
import { Section, Eyebrow } from "./Primitives";
import { FadeUp } from "./Reveal";
import { CTA } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function GetLoopedIn() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("That email doesn't look right — mind checking it?");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("You're in the loop. We'll reply within one business day.");
      setForm({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something broke on our end. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-0 border-b border-navy/25 bg-transparent pb-2.5 pt-1 font-display text-lg text-navy outline-none transition-colors placeholder:text-navy/35 focus:border-navy";
  const labelCls = "mb-2 block font-mono-c text-[0.66rem] uppercase tracking-[0.16em] text-navy/60";

  return (
    <Section id="looped-in" className="py-20 md:py-28">
      <FadeUp>
        <div className="relative overflow-hidden rounded-[2rem] bg-brand px-6 py-16 text-navy md:px-16 md:py-20">
          {/* ghost mark inside the card */}
          <img
            src="/runloop_ghost.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -top-12 h-56 w-auto object-contain opacity-[0.09] brightness-0 md:h-72"
          />

          <div className="relative flex max-w-2xl flex-col items-start text-left">
            <Eyebrow>{CTA.eyebrow}</Eyebrow>
            <h2 className="mt-7 font-display text-5xl font-light tracking-[-0.015em] text-navy sm:text-6xl md:text-7xl">
              <span className="font-light">Get</span> <span className="emph italic">looped</span> <span className="font-light">in.</span>
            </h2>
            <p className="mt-7 max-w-xl font-body text-lg leading-relaxed text-navy/70">{CTA.body}</p>
          </div>

          <form onSubmit={submit} noValidate data-testid="contact-form" className="relative mt-14 flex max-w-2xl flex-col gap-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <label className="block text-left">
                <span className={labelCls}>Your name <span className="text-navy">*</span></span>
                <input type="text" value={form.name} onChange={update("name")} placeholder="Rahul Mehta" data-testid="contact-name" className={inputCls} />
              </label>
              <label className="block text-left">
                <span className={labelCls}>Work email <span className="text-navy">*</span></span>
                <input type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" data-testid="contact-email" className={inputCls} />
              </label>
            </div>
            <label className="block text-left">
              <span className={labelCls}>Company</span>
              <input type="text" value={form.company} onChange={update("company")} placeholder="Where you work" data-testid="contact-company" className={inputCls} />
            </label>
            <label className="block text-left">
              <span className={labelCls}>Where does the manual work live? <span className="text-navy">*</span></span>
              <textarea
                rows={3}
                value={form.message}
                onChange={update("message")}
                placeholder="e.g. Our BD team copies tenders between five tools every morning..."
                data-testid="contact-message"
                className={`${inputCls} resize-none text-base leading-relaxed`}
              />
            </label>

            <div className="mt-2 flex justify-start">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 rounded-full bg-[#111318] py-2 pl-7 pr-2 font-display text-sm font-semibold tracking-tight text-paper transition-all duration-300 hover:-translate-y-[2px] disabled:opacity-60"
              >
                {loading ? "Sending…" : done ? "Sent" : "Let's Talk"}
                <span className="grid h-9 w-9 place-items-center rounded-full bg-brand text-navy transition-transform duration-300 group-hover:rotate-45">
                  {loading ? <Loader2 size={15} className="animate-spin" /> : done ? <Check size={15} /> : <ArrowUpRight size={16} strokeWidth={2.4} />}
                </span>
              </button>
            </div>
          </form>
        </div>
      </FadeUp>
    </Section>
  );
}
